import { useEffect, useRef, useState } from "react";

/**
 * Watch a bottom sentinel and call `onReach` when it comes within `rootMargin` of the viewport.
 * Returns the ref to put on the sentinel element (`<div ref={setSentinel} />`).
 *
 * **Why the node lives in state instead of a ref.** A pagination sentinel is conditionally
 * rendered — only while there are more pages — so it very often mounts in a LATER commit than the
 * one that decided "there is more". An effect that reads `sentinelRef.current` and lists only the
 * pagination inputs in its deps then runs while the node is still `null`, bails, and never runs
 * again: the sentinel sits in the DOM, `hasMore` is true, and *nothing is observing it*. Pagination
 * is silently dead, with no error and no failed request to notice.
 *
 * That is the second half of REF-6141, measured on production. `MoodboardsPanel`'s loader flushed
 * `totalPages`/`page` BEFORE awaiting cover enrichment and `setIsLoading(false)` AFTER it, so a page
 * whose rows needed a cover fetch committed twice — `hasMore` flipped true in the first commit
 * (spinner still up, sentinel unmounted → effect bailed on a null ref) and the sentinel mounted in
 * the second (no dep changed → effect never re-ran). Accounts whose rows all carried inline covers
 * took `enrichWithImages`' no-fetch fast path, so both updates landed in ONE React batch and
 * paging worked — which is exactly why the bug presented as "it works on my account, not hers".
 *
 * Keying on the node removes the whole class: mounting the sentinel *is* the dependency change.
 *
 * `onReach` is held in a ref, so a caller may pass an inline arrow without re-creating the
 * observer every render. That matters twice over: a fresh `IntersectionObserver` immediately
 * reports the current intersection state, so churning it re-fires `onReach`; and reading the
 * latest closure is what keeps `page` / `activeTab` inside the callback from going stale.
 */
export const useSentinelObserver = <T extends Element = HTMLDivElement>(onReach: () => void, { enabled = true, rootMargin = "200px" }: { enabled?: boolean; rootMargin?: string } = {}) => {
  const [sentinel, setSentinel] = useState<T | null>(null);
  const onReachRef = useRef(onReach);
  onReachRef.current = onReach;

  useEffect(() => {
    if (!sentinel || !enabled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onReachRef.current();
      },
      { rootMargin }
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [sentinel, enabled, rootMargin]);

  return setSentinel;
};

export default useSentinelObserver;
